/**
 * FIX-3b (UX-SPEC.md section 3.3, task T5): the ONE stronger setting "Make it smaller" pre-selects in the Re-edit modal.
 *
 * Rules, in order (the first that the page actually offers wins):
 *   1. WAV output whose codec is not already MP3. The spec says "16-bit WAV -> MP3 inside WAV"; the browser has no probe of
 *      the source, so the bitrate is left to the edge, which has one (UX3-02, validation round 3: MP3 inside WAV alone
 *      revealed File Size Percentage at 80, which the edge read as 80 % of a 16-bit source's 1411 kbps -- 1129 kbps, capped
 *      at 320 with a "1129 kbps is not possible" note, against a modal estimate of 80 % of the original):
 *      a. Auto (`audioCodec=AUTO`, "Auto — smallest file") when the page offers it, `featureFlags.compressNoGainPolicy` is
 *         exactly true (the edge reads AUTO as PCM 16-bit otherwise) and so is `featureFlags.compressWavAutoDefault` (the
 *         page shows Auto only then, UXR1-01), and the run was on 8/16-bit PCM. The edge then
 *         stores 8/16-bit PCM as MP3 inside WAV at min(192 kbps, half the source, MP3's ceiling at the rate); no percentage
 *         or size control is shown, so there is no number to contradict.
 *         FU2-04 (fu-r3): not when the edge says Auto would only keep this run's source (`MakeSmallerHints`, from the size
 *         note's `params.wavAuto = keep_original`: an 8 kHz 8-bit, A-law or low-rate upload). Auto would hand the same
 *         bytes back ("Original kept") and the user's press would make nothing smaller; rule 1b is the step that does.
 *      b. Otherwise MP3 inside WAV at a percentage of the original size (`audioCodec=MP3` + the percentage mode).
 *         - A run on PCM deeper than 16-bit (QA4-06, round 4): Auto would only trim the (equally deep) source to PCM
 *           16-bit -- 67 % of a 24-bit file, not "the smallest file" -- so it is not offered. The percentage is the one
 *           that, on a 44.1 / 48 kHz source of that depth, asks the edge for about 170-185 kbps stereo (85-92 mono):
 *           8 % of 24-bit, 6 % of 32-bit. That is under MP3's 320 kbps ceiling, so the edge encodes what was asked, writes
 *           no "not possible" note, and the modal's estimate holds. (Above 48 kHz the edge may still cap it; the web has
 *           no probe of the source rate.)
 *         - Anything else at 50 %. A run that Auto itself kept, or one on A-law / mu-law, comes from a source under
 *           128 kbps (Auto keeps a source whose half is under 64 kbps): half of it is under every MP3 ceiling, so the edge
 *           encodes what was asked and the estimate holds. Without Auto this is still the gentler request of the two
 *           percentages the page can post.
 *   2. Pages that offer `sizePercentage`: 50 % of the original size -- by lowering the current percentage when the run
 *      used one above 50, or by switching the compression mode to the page's percentage mode otherwise. A run that
 *      already asked for <= 50 % gets nothing stronger from this rule.
 *   3. Otherwise the next lower quality preset (`*QualityPreset`, ladder HIGHEST .. LOWEST) the page shows.
 *   4. Else none: the button is hidden.
 *
 * "Offers" is decided by the real option engine, not by reading the tree's `hidden` clauses here: each candidate is
 * applied to a fresh `ConvertOptionsContextV2` built from the page's tree plus the card's own options, and it counts only
 * if the resulting summary (what Apply & Restart would post) carries it. A hidden or absent choice falls back to the
 * group's default in that summary, so it can never be suggested.
 *
 * PDF is not covered: its levels are a separate, flag-gated ladder (pdf-compress-presets.gate.ts) and Screen, the
 * default, is already the strongest level shown while the flag is off.
 */
import { ConvertOptionsContextV2, ConvertOptionsSummary } from '../../convert-options-v2/convert-options.context';
import type { ViewState } from '../../convert-options-v2/convert-options.view-states';

export type MakeSmallerRule = 'wav_auto' | 'wav_mp3' | 'size_percentage' | 'quality_preset';

/**
 * FU2-04 (fu-r3): what the edge knew about the run's source that the browser cannot probe. Read off the card's result by
 * `makeSmallerHintsOf`; each counts only when exactly `true`.
 */
export interface MakeSmallerHints {
    /** Auto (`audioCodec=AUTO`) would keep this source unchanged, so it is no "smaller" step. */
    wavAutoKeepsOriginal?: boolean;
}

/** Ironman `NoGainPolicy.PARAM_WAV_AUTO` / `WAV_AUTO_KEEPS_ORIGINAL`: the hint rides on the size note's params. */
export const WAV_AUTO_HINT_PARAM = 'wavAuto';
export const WAV_AUTO_KEEPS_ORIGINAL = 'keep_original';

/** The hints a card's `result.noteCodes` carry (none on a result from an older edge). Never throws. */
export function makeSmallerHintsOf(result: { noteCodes?: unknown } | null | undefined): MakeSmallerHints {
    const codes = result?.noteCodes;
    if (!Array.isArray(codes)) return {};
    const keeps = codes.some((c: any) => c && typeof c === 'object' && c.params && c.params[WAV_AUTO_HINT_PARAM] === WAV_AUTO_KEEPS_ORIGINAL);
    return keeps ? { wavAutoKeepsOriginal: true } : {};
}

/** The site flags the suggestion reads (settings `featureFlags`); each counts only when exactly `true`. */
export interface MakeSmallerFlags {
    compressNoGainPolicy?: unknown;
    /** UXR1-01: the compress-wav page offers Auto only while this is on too. */
    compressWavAutoDefault?: unknown;
}

/**
 * The C6 `suggestion` shape (`{optionKey, value, label}`) plus `patch`: every key Apply & Restart must change for the
 * suggestion (a mode switch carries the mode AND its value), merged over the card's options by the Re-edit modal.
 */
export interface MakeSmallerSuggestion {
    optionKey: string;
    /** A string, as C6's `ReeditSuggestion.value` (`reedit-footer-mode.ts`); the typed value Apply posts is in `patch`. */
    value: string;
    label: string;
    patch: ConvertOptionsSummary;
    rule: MakeSmallerRule;
}

/** Human labels for the two fixed suggestions (UX-SPEC 3.3 wording). Rule 3 reads its label off the tree. */
export const MAKE_SMALLER_LABELS = {
    /** Rule 1a falls back to this when the tree's Auto choice has no name. */
    wavAuto: 'Auto — smallest file',
    /** Rule 1b changes two settings (codec and percentage), so the line names both. */
    wavMp3: 'MP3 inside WAV at 50% of the original size',
    /** Rule 1b on a run deeper than 16-bit (QA4-06): `{pct}` is `DEEP_PCM_SIZE_PERCENTAGE` for the run's codec. */
    wavMp3Deep: 'MP3 inside WAV at {pct}% of the original size',
    sizePercentage: '50% of the original size',
    /** `{preset}` is the tree's own option name without its parenthetical ("Very High (Recommended)" -> "Very High"). */
    qualityPreset: '{preset} quality',
} as const;

export const SUGGESTED_SIZE_PERCENTAGE = 50;

/** The quality ladder every compress tree uses for `*QualityPreset` (checked across all compress pages, 2026-10-02). */
export const QUALITY_LADDER = ['HIGHEST', 'VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'VERY_LOW', 'LOWEST'] as const;

/** Percentage modes, preferred first (image/video: auto-scale; audio: plain). */
const PERCENTAGE_MODES = ['FILE_SIZE_PERCENTAGE_WITH_AUTO_SCALE', 'FILE_SIZE_PERCENTAGE'] as const;
/** The property that holds the compression mode: video trees use `videoCompression`, image/audio `compression`. */
const MODE_KEYS = ['videoCompression', 'compression'] as const;

type Tree = any;

/** A fresh context: the tree, then the card's options. Never shares state between probes. Throws on a malformed tree. */
function contextFor(tree: Tree, options: ConvertOptionsSummary | null | undefined): ConvertOptionsContextV2 {
    const ctx = ConvertOptionsContextV2.newContext(JSON.parse(JSON.stringify(tree)));
    if (options) ctx.applySummary(options);
    return ctx;
}

/** What Apply & Restart would post after applying `patch` over the card's options. */
function probe(tree: Tree, options: ConvertOptionsSummary | null | undefined, patch: ConvertOptionsSummary): ConvertOptionsSummary {
    const ctx = contextFor(tree, options);
    ctx.applySummary({ ...(options || {}), ...patch });
    return ctx.getSummary();
}

const sameValue = (a: unknown, b: unknown) => a !== undefined && a !== null && String(a) === String(b);

/** The tree's display name for a dropdown value under a `*QualityPreset` subgroup, or null. */
function presetOptionName(tree: Tree, propertyName: string, value: string): string | null {
    let found: string | null = null;
    const walk = (node: any, inSubgroup: boolean) => {
        if (found || !node || typeof node !== 'object') return;
        const here = inSubgroup || (node.uiType === 'SUBGROUP' && Array.isArray(node.propertyNames) && node.propertyNames.includes(propertyName));
        if (here && node.uiType === 'DROPDOWN_OPTION' && node.value === value && typeof node.name === 'string') {
            found = node.name;
            return;
        }
        (node.options || []).forEach((child: any) => walk(child, here));
    };
    walk(tree, false);
    return found;
}

function humanPreset(name: string | null, value: string): string {
    const base = (name ?? value.toLowerCase().replace(/_/g, ' ')).replace(/\s*\(.*?\)\s*/g, ' ').trim();
    return base.charAt(0).toUpperCase() + base.slice(1);
}

/** Linear PCM codecs a WAV run can be on that Auto shrinks most (it stores 8/16-bit PCM as MP3 inside WAV). */
const AUTO_IMPROVES = ['PCM_S16LE', 'PCM_S16BE', 'PCM_U8', 'PCM_S8'];

/**
 * QA4-06: PCM deeper than 16-bit, and the MP3-inside-WAV percentage rule 1b asks for on such a run -- about 170-185 kbps
 * from a 44.1 / 48 kHz stereo source of that depth (24-bit 48 kHz stereo is 2304 kbps: 8 % = 184; 32-bit: 3072, 6 % = 184).
 * Auto is never suggested for these: it trims a deep source to PCM 16-bit (T4), which is not the smallest file.
 */
export const DEEP_PCM_SIZE_PERCENTAGE: Readonly<Record<string, number>> = {
    PCM_S24LE: 8, PCM_S24BE: 8,
    PCM_S32LE: 6, PCM_S32BE: 6, PCM_F32LE: 6, PCM_F32BE: 6,
};

/** The tree's display name for the choice that writes `audioCodec=<value>` (a GROUP_SELECTION), or null. */
function codecChoiceName(tree: Tree, value: string): string | null {
    let found: string | null = null;
    const walk = (node: any, inCodecGroup: boolean) => {
        if (found || !node || typeof node !== 'object') return;
        const here = inCodecGroup || (node.uiType === 'GROUP' && Array.isArray(node.propertyNames) && node.propertyNames.includes('audioCodec'));
        if (here && node.uiType === 'GROUP_SELECTION' && node.propertyValue === value && typeof node.name === 'string') {
            found = node.name;
            return;
        }
        (node.options || []).forEach((child: any) => walk(child, here));
    };
    walk(tree, false);
    return found;
}

const isWav = (summary: ConvertOptionsSummary) => String(summary.toExtension ?? '').toUpperCase() === 'WAV';

function wavAuto(
    tree: Tree,
    options: ConvertOptionsSummary | null | undefined,
    base: ConvertOptionsSummary,
    flags: MakeSmallerFlags | null | undefined,
    hints: MakeSmallerHints | null | undefined,
): MakeSmallerSuggestion | null {
    if (flags?.compressNoGainPolicy !== true || flags?.compressWavAutoDefault !== true || !isWav(base)) return null;
    if (hints?.wavAutoKeepsOriginal === true) return null;
    if (!AUTO_IMPROVES.includes(String(base.audioCodec ?? '').toUpperCase())) return null;
    const patch = { audioCodec: 'AUTO' };
    const after = probe(tree, options, patch);
    if (!sameValue(after.audioCodec, 'AUTO') || !isWav(after)) return null;
    const name = codecChoiceName(tree, 'AUTO');
    const label = name ? name.replace(/\s*\(.*?\)\s*/g, ' ').trim() : MAKE_SMALLER_LABELS.wavAuto;
    return { optionKey: 'audioCodec', value: 'AUTO', label, patch, rule: 'wav_auto' };
}

function wavMp3(tree: Tree, options: ConvertOptionsSummary | null | undefined, base: ConvertOptionsSummary): MakeSmallerSuggestion | null {
    if (!isWav(base) || sameValue(base.audioCodec, 'MP3')) return null;
    const deep = DEEP_PCM_SIZE_PERCENTAGE[String(base.audioCodec ?? '').toUpperCase()];
    const target = deep ?? SUGGESTED_SIZE_PERCENTAGE;
    const label = deep ? MAKE_SMALLER_LABELS.wavMp3Deep.replace('{pct}', String(deep)) : MAKE_SMALLER_LABELS.wavMp3;
    // MP3 inside WAV reveals the compression group; the percentage mode is its default, and an explicit mode covers a run
    // that carried another one.
    for (const patch of [
        { audioCodec: 'MP3', sizePercentage: target },
        ...PERCENTAGE_MODES.map(mode => ({ audioCodec: 'MP3', compression: mode, sizePercentage: target })),
    ]) {
        const after = probe(tree, options, patch);
        if (sameValue(after.audioCodec, 'MP3') && isWav(after) && sameValue(after.sizePercentage, target)) {
            return { optionKey: 'audioCodec', value: 'MP3', label, patch, rule: 'wav_mp3' };
        }
    }
    return null;
}

function sizePercentage(tree: Tree, options: ConvertOptionsSummary | null | undefined, base: ConvertOptionsSummary): MakeSmallerSuggestion | null {
    const target = SUGGESTED_SIZE_PERCENTAGE;
    const accept = (patch: ConvertOptionsSummary, modeKey?: string): MakeSmallerSuggestion | null => {
        const after = probe(tree, options, patch);
        if (!sameValue(after.sizePercentage, target)) return null;
        if (modeKey && !sameValue(after[modeKey], patch[modeKey] ?? base[modeKey])) return null;
        return { optionKey: 'sizePercentage', value: String(target), label: MAKE_SMALLER_LABELS.sizePercentage, patch, rule: 'size_percentage' };
    };
    const modeKey = MODE_KEYS.find(k => base[k] !== undefined);
    if (base.sizePercentage !== undefined && base.sizePercentage !== '') {
        // The run already used a percentage: only a lower one is stronger.
        const current = Number(base.sizePercentage);
        if (!Number.isFinite(current) || current <= target) return null;
        return accept({ sizePercentage: target }, modeKey);
    }
    if (!modeKey) return null;
    for (const mode of PERCENTAGE_MODES) {
        const hit = accept({ [modeKey]: mode, sizePercentage: target }, modeKey);
        if (hit) return hit;
    }
    return null;
}

function qualityPreset(tree: Tree, options: ConvertOptionsSummary | null | undefined, base: ConvertOptionsSummary): MakeSmallerSuggestion | null {
    const key = Object.keys(base).find(k => /QualityPreset$/.test(k) && typeof base[k] === 'string');
    if (!key) return null;
    const at = (QUALITY_LADDER as readonly string[]).indexOf(String(base[key]));
    if (at < 0) return null;
    for (const next of QUALITY_LADDER.slice(at + 1)) {
        const patch = { [key]: next };
        const after = probe(tree, options, patch);
        if (!sameValue(after[key], next)) continue;
        const label = MAKE_SMALLER_LABELS.qualityPreset.replace('{preset}', humanPreset(presetOptionName(tree, key, next), next));
        return { optionKey: key, value: next, label, patch, rule: 'quality_preset' };
    }
    return null;
}

/**
 * The suggestion for a card run with `options` on the page whose option tree is `tree`, or `null` (hide the button).
 * `flags` are the site's settings flags (only `compressNoGainPolicy` and `compressWavAutoDefault` are read, for rule 1a); `hints` what the edge said about
 * the run's source (`makeSmallerHintsOf(result)`, rule 1a). Never throws: a tree the engine cannot read means no suggestion.
 */
export function suggestMakeSmaller(
    tree: Tree,
    options: ConvertOptionsSummary | null | undefined,
    flags?: MakeSmallerFlags | null,
    hints?: MakeSmallerHints | null,
): MakeSmallerSuggestion | null {
    if (!tree || typeof tree !== 'object') return null;
    try {
        const base = contextFor(tree, options).getSummary();
        if (String(base.process ?? '').toUpperCase() !== 'COMPRESS') return null;
        return wavAuto(tree, options, base, flags, hints) ?? wavMp3(tree, options, base) ?? sizePercentage(tree, options, base) ?? qualityPreset(tree, options, base);
    } catch {
        return null;
    }
}

/**
 * The ids of the page's top-level option groups that hold `optionKey` (UX1-07): the Re-edit modal marks these and scrolls
 * the one on screen into view, so "You can change it" points at a setting the user can see. Read off the context the
 * modal renders (`getViewStates` maps a property to every view state that writes it); nested properties (the percentage
 * under a compression mode) resolve to their top-level group. A page can have several (WAV's generic and WAV-only codec
 * groups); the options component renders only the visible one, so only that one is ever marked. Hiding is left to the
 * component on purpose -- its rules (shared properties) are not re-derived here. Empty when the key is unknown; never
 * throws.
 */
export function suggestedGroupIds(ctx: ConvertOptionsContextV2 | null | undefined, optionKey: string | null | undefined): string[] {
    if (!ctx || !optionKey) return [];
    try {
        const topLevel = new Set<ViewState>((ctx.getModel()?.options ?? []) as ViewState[]);
        const ids: string[] = [];
        for (const vs of (ctx.getViewStates(optionKey) ?? []) as ViewState[]) {
            let cur: ViewState | undefined = vs;
            while (cur && !topLevel.has(cur)) cur = cur.parent;
            if (cur && cur.id !== undefined && cur.id !== null && !ids.includes(String(cur.id))) ids.push(String(cur.id));
        }
        return ids;
    } catch {
        return [];
    }
}
